import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Bot } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WELCOME = "Hi! I'm Volt, the Electronico AI assistant. We operate across Electronic Security, Contract Manufacturing and Multi Media Marketing. What can I help you with?";

// Volt AI chat widget wired to the Gemini-powered backend (/api/chat).
export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now())));
  const [messages, setMessages] = useState([
    { role: 'bot', text: WELCOME },
  ]);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const data = await res.json();
      setMessages((m) => [...m, { role: 'bot', text: data.reply || "Sorry, I couldn't respond just now." }]);
    } catch (err) {
      setMessages((m) => [...m, { role: 'bot', text: 'Network hiccup — please try again in a moment.' }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 60 }}
        className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#0ef3fe] to-[#088ffe] flex items-center justify-center glow-cyan hover:scale-110 transition-transform"
        aria-label="Chat with Volt AI"
        data-testid="chatbot-launcher-button"
      >
        {open ? <X className="h-6 w-6 text-black" /> : <MessageSquare className="h-6 w-6 text-black" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          style={{ position: 'fixed', bottom: '96px', right: '24px', zIndex: 60, width: 'min(384px, 92vw)', height: '520px' }}
          className="neon-border rounded-3xl bg-[#0f1c30]/95 backdrop-blur-xl flex flex-col overflow-hidden shadow-2xl"
        >
          <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-gradient-to-r from-[#0ef3fe]/10 to-[#088ffe]/10">
            <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0ef3fe] to-[#088ffe] flex items-center justify-center"><Bot className="h-5 w-5 text-black" /></span>
            <div>
              <div className="font-display font-bold leading-tight">Volt AI</div>
              <div className="text-xs text-white/50 flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[#5bd0ff]" /> Online · Electronico assistant</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div data-testid={`chatbot-message-${m.role}`} className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-gradient-to-br from-[#0ef3fe] to-[#0a6bd6] text-black font-medium'
                    : 'bg-white/5 text-white/85 border border-white/10'
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 flex gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#0ef3fe] animate-pulse-glow" />
                  <span className="h-2 w-2 rounded-full bg-[#0a6bd6] animate-pulse-glow" style={{ animationDelay: '0.2s' }} />
                  <span className="h-2 w-2 rounded-full bg-[#088ffe] animate-pulse-glow" style={{ animationDelay: '0.4s' }} />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about our divisions..."
              className="flex-1 bg-white/5 border border-white/15 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#0ef3fe]/60"
              data-testid="chatbot-input"
            />
            <button type="submit" disabled={busy} className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0ef3fe] to-[#088ffe] flex items-center justify-center disabled:opacity-50" data-testid="chatbot-send-button">
              <Send className="h-4 w-4 text-black" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
